import http from "../helpers/http";
import {
  getExternalSystemParams,
  getOvdimBariMultiExternalSystemParams,
  removeIsSubsidyOver,
  saveIsSubsidyOver,
} from "../utils/partner";

class ClubAPI {
  /**
   * Handle checking if subsidy is over from response
   * @param {Object} response - API response object
   * @param {Object} response.data - Response data
   * @param {number} response.data.Error - Error code
   * @param {string} response.data.Data - JSON string containing response data
   * @private
   */
  #handleSubsidyOverCheck = (response) => {
    if (response.data.Error === 0 && response.data.Data) {
      const data = JSON.parse(response.data.Data);
      if (data.IsSubsidyOver) {
        saveIsSubsidyOver(data.IsSubsidyOver);
      } else {
        removeIsSubsidyOver();
      }
    }
  };

  /**
   * Get clubs by category
   * @param {Object} params - Parameters for the request
   * @param {string} params.binID - User's bin ID
   * @param {string} params.mainCatID - Main category ID
   * @param {string} params.subCatID - Sub category ID
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing clubs in the category
   */
  async getClubsByCategory({ binID, mainCatID, subCatID = "0", token, id, phone, cityId = null, isFreefitWeb = true }) {
    try {
      const payload = {
        BinID: binID,
        MainCatID: mainCatID,
        SubCatID: subCatID,
        Token: token,
        ID: id,
        Phone: phone,
      };
      if (cityId) {
        payload.CityId = cityId;
        payload.IsFreefitWeb = isFreefitWeb;
      }
      const response = await http.post("GetMobileClubsIdsByCategory", payload);
      return response;
    } catch (error) {
      console.error("Failed to get clubs by category:", error);
      throw error;
    }
  }

  /**
   * Get clubs by category
   * @param {Object} params - Parameters for the request
   * @param {string} params.binID - User's bin ID
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @param {string} params.token - User token
   * @param {string} params.cityId - Chosen city ID
   * @returns {Promise<List<Object>>} Response containing list of categories of a specific city
   */
  async getClubsByCategoryByCityId({ binID, id, phone, token, cityId }) {
    try {
      const response = await http.post("GetClubCategoryListByCityId", {
        BinID: binID,
        ID: id,
        Phone: phone,
        Token: token,
        CityId: cityId,
      });
      return response;
    } catch (error) {
      console.error("Failed to get clubs by category:", error);
      throw error;
    }
  }

  /**
   * Get all clubs and cities data
   * @param {Object} params - Parameters for the request
   * @param {string} params.binID - User's bin ID
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing all clubs and cities data
   */
  async getWebFullData({ binID, token, id, phone }) {
    try {
      const response = await http.post("GetWebFullData", {
        BinID: binID,
        Token: token,
        ID: id,
        Phone: phone,
        ...getExternalSystemParams(),
      });
      this.#handleSubsidyOverCheck(response);
      return response;
    } catch (error) {
      console.error("Failed to get web full data:", error);
      throw error;
    }
  }

  /**
   * Get club category list
   * @param {Object} params - Parameters for the request
   * @param {string} params.binID - User's bin ID
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing club categories
   */
  async getClubCategoryList({ binID, token, id, phone }) {
    try {
      const response = await http.post("GetClubCategoryList", {
        BinID: binID,
        Token: token,
        ID: id,
        Phone: phone,
        IsFreefitWeb: true,
      });
      return response;
    } catch (error) {
      console.error("Failed to get club category list:", error);
      throw error;
    }
  }

  /**
   * Get order time range for a club
   * @param {Object} params - Parameters for the request
   * @param {string} params.clubID - Club ID
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing order time range
   */
  async getOrderTimeRange({ clubID, token, id, phone }) {
    try {
      const response = await http.post("GetOrderTimeRange", {
        ClubID: clubID,
        Token: token,
        ID: id,
        Phone: phone,
        IsFreefitWeb: true,
        ...getExternalSystemParams(),
      });
      return response;
    } catch (error) {
      console.error("Failed to get order time range:", error);
      throw error;
    }
  }

  /**
   * Creates a web club order
   * @param {Object} params - The parameters for creating the order
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone
   * @param {string} params.cardNumber - Card number
   * @param {string} params.terminalID - Terminal ID
   * @param {string} params.binType - Bin type
   * @param {string} params.clubID - Club ID
   * @param {string} [params.entryCount] - Entry count (default: "1")
   * @param {string} params.preOrderDate - Pre-order date
   * @param {boolean} params.isRbox - Is Rbox
   * @param {string} [params.rboxLessonID] - Rbox lesson ID (for book type)
   * @param {string} [params.lessonName] - Lesson name (for book type)
   * @param {string} [params.lessonStartDate] - Lesson start date (for book type)
   * @param {string} [params.lessonEndDate] - Lesson end date (for book type)
   * @param {string} [params.coachName] - Coach name (for book type)
   * @param {string} [params.cancelationTime] - Cancellation time (for book type)
   * @param {boolean} [params.isCancelAllow] - Is cancellation allowed (for book type)
   * @param {boolean} [params.isOnlyCheckWithoutTransaction] - Is only check without transaction
   * @returns {Promise<Object>} The response from the server
   */
  async createWebClubOrder(params) {
    try {
      const requestParams = {
        Token: params.token,
        ID: params.id,
        Phone: params.phone,
        CardNumber: params.cardNumber,
        TerminalID: params.terminalID,
        BinType: params.binType,
        ClubID: params.clubID,
        EntryCount: params.entryCount || "1",
        PreOrderDate: params.preOrderDate,
        IsRbox: params.isRbox,
        PushToken: null,
        AppVer: null,
        ...getExternalSystemParams(),
      };

      // Only add book-specific parameters if they exist
      if (params.rboxLessonID) {
        Object.assign(requestParams, {
          RboxLessonID: params.rboxLessonID,
          LessonName: params.lessonName,
          LessonStartDate: params.lessonStartDate,
          LessonEndDate: params.lessonEndDate,
          CoachName: params.coachName,
          CancelationTime: params.cancelationTime,
          IsCancelAllow: params.isCancelAllow,
        });
      }

      if (params.source) {
        Object.assign(requestParams, {
          Source: params.source,
        });
      }

      // Only add isOnlyCheckWithoutTransaction if it's explicitly provided
      if (typeof params.isOnlyCheckWithoutTransaction === "boolean") {
        requestParams.IsOnlyCheckWithoutTransaction = params.isOnlyCheckWithoutTransaction;
      }

      const response = await http.post("WebClubOrder", requestParams);
      return response;
    } catch (error) {
      console.error("Error creating web club order:", error);
      throw error;
    }
  }

  /**
   * Get web club orders
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing web club orders
   */
  async getWebClubOrders({ token, id, phone }) {
    try {
      const response = await http.post("GetWebClubOrders", {
        PushToken: null,
        AppVer: null,
        Token: token,
        ID: id,
        Phone: phone,
        ...getOvdimBariMultiExternalSystemParams(),
      });
      return response;
    } catch (error) {
      console.error("Failed to get web club orders:", error);
      throw error;
    }
  }

  /**
   * Cancel a web club order
   * @param {Object} params - Parameters for the request
   * @param {string} params.clubOrderNum - Club order number to cancel
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response from the server
   */
  async cancelWebClubOrder({ clubOrderNum, token, id, phone }) {
    try {
      const response = await http.post("CancelWebClubOrder", {
        ClubOrderNum: clubOrderNum,
        Token: token,
        ID: id,
        Phone: phone,
        ...getOvdimBariMultiExternalSystemParams(),
      });
      return response;
    } catch (error) {
      console.error("Failed to cancel web club order:", error);
      throw error;
    }
  }

  /**
   * Get web client entries history
   * @param {Object} params - Parameters for the request
   * @param {string} params.binID - User's bin ID
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing client's entry history
   */
  async getWebClientEntriesHistory({ binID, token, id, phone }) {
    try {
      const response = await http.post("GetWebClientEntriesHistory", {
        BinID: binID,
        Token: token,
        ID: id,
        Phone: phone,
        ...getOvdimBariMultiExternalSystemParams(),
      });
      return response;
    } catch (error) {
      console.error("Failed to get web client entries history:", error);
      throw error;
    }
  }

  /**
   * Get club lesson list
   * @param {Object} params - Parameters for the request
   * @param {string} params.clubID - Club ID
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing club lesson list
   */
  async getClubLessonList({ clubID, token, id, phone }) {
    try {
      const response = await http.post("GetClubLessonList", {
        ClubID: clubID + "",
        Token: token,
        ID: id,
        Phone: phone,
        IsFreefitWeb: true,
        ...getExternalSystemParams(),
      });
      return response;
    } catch (error) {
      console.error("Failed to get club lesson list:", error);
      throw error;
    }
  }

  /**
   * Create a web club entry
   * @param {Object} params - Parameters for the request
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @param {string} params.cardNumber - Card number
   * @param {string} params.terminalID - Terminal ID
   * @param {string} params.clubID - Club ID
   * @param {string} [params.entryCount="1"] - Entry count
   * @param {string} params.binType - Bin type
   * @param {boolean} [params.isOnlyCheckWithoutTransaction=false] - Whether to only check without transaction
   * @returns {Promise<Object>} Response from the server
   */
  async webClubEntry({
    token,
    id,
    phone,
    cardNumber,
    source,
    terminalID,
    clubID,
    entryCount = "1",
    binType,
    isOnlyCheckWithoutTransaction = false,
  }) {
    try {
      const requestParams = {
        Token: token,
        ID: id,
        Phone: phone,
        CardNumber: cardNumber,
        TerminalID: terminalID,
        ClubID: clubID,
        EntryCount: entryCount,
        BinType: binType,
        IsOnlyCheckWithoutTransaction: isOnlyCheckWithoutTransaction,
        ...getExternalSystemParams(),
      };

      if (source) {
        Object.assign(requestParams, {
          Source: source,
        });
      }

      const response = await http.post("WebClubEntry", requestParams);

      return response;
    } catch (error) {
      console.error("Failed to create web club entry:", error);
      throw error;
    }
  }

  /**
   * Get detailed club information by ID
   * @param {Object} params - Parameters for the request
   * @param {string} params.clubID - Club ID
   * @param {string} params.token - User token
   * @param {string} params.id - User ID
   * @param {string} params.phone - User phone number
   * @returns {Promise<Object>} Response containing detailed club information
   */
  async getDetailedClubInfoByID({ clubID, token, id, phone }) {
    try {
      const response = await http.post("GetDetailedClubInfoByID", {
        ClubID: clubID,
        Token: token,
        ID: id,
        Phone: phone,
      });
      return response;
    } catch (error) {
      console.error("Failed to get detailed club info:", error);
      throw error;
    }
  }
}

const clubAPI = new ClubAPI();
export default clubAPI;
